import { ReactElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../../client';
import { __ } from '../../../../i18n/l10n';
import SoundPlayer from '../../../../singletons/SoundPlayer';
import { SoundIds } from '../../../../types/sounds';
import { GRCheckSvg, GRCrossSvg, GRGiftSvg, GRTimerSvg } from '../../assets/svg';
import { DeltaTransitionsDurations } from '../../constants/delta';
import useGRRoundContext from '../../hooks/useGRRoundContext';
import { type GRChipStatus, GRChipStatuses } from '../../types/chip';
import { GRNotificationTypes } from '../../types/notification';

const BackgroundColorByStatus = {
  [GRChipStatuses.WON]: '#1ea924',
  [GRChipStatuses.LOST]: '#f53e24',
  [GRChipStatuses.PENDING]: '#ffc511',
};

const ColorizedIconValuesByStatus = {
  [GRChipStatuses.WON]: BackgroundColorByStatus[GRChipStatuses.WON],
  [GRChipStatuses.PENDING]: BackgroundColorByStatus[GRChipStatuses.PENDING],
  [GRChipStatuses.LOST]: BackgroundColorByStatus[GRChipStatuses.LOST],
};

type ColorizedIconSharedStyleProps = {
  $colorized: boolean;
  $status: GRChipStatus;
};
const colorizedIconSharedStyle = css<ColorizedIconSharedStyleProps>`
  ${({ $colorized, $status }) => css`
    color: ${$colorized ? ColorizedIconValuesByStatus[$status] : 'white'};
  `}
`;

const chipIconsStyleShared = css`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 35%;

  transform: translate(-50%, -30%);
`;

const GRCrossIcon = styled(GRCrossSvg)`
  ${colorizedIconSharedStyle}
  ${chipIconsStyleShared}
  width: 30%;
`;
const GRCheckIcon = styled(GRCheckSvg)`
  ${colorizedIconSharedStyle}
  ${chipIconsStyleShared}
`;
const GRTimerIcon = styled(GRTimerSvg)`
  ${colorizedIconSharedStyle}
  ${chipIconsStyleShared}
`;

type GRChipIconPropsType = {
  status: GRChipStatus;
  counted: boolean;
};
const GRChipIcon = ({ status, counted }: GRChipIconPropsType) => {
  const colorized = !counted;

  if (status === GRChipStatuses.WON) {
    return <GRCheckIcon $colorized={colorized} $status={status} />;
  }

  if (status === GRChipStatuses.LOST) {
    return <GRCrossIcon $colorized={colorized} $status={status} />;
  }

  if (status === GRChipStatuses.PENDING) {
    return <GRTimerIcon $colorized={colorized} $status={status} />;
  }

  return null;
};

type GetBackgroundProps = {
  $status?: GRChipStatus;
  $counted: boolean;
};

const getBackground = ({ $status, $counted }: GetBackgroundProps) => {
  const defaultBg = '#000000aa';

  if (!$status) {
    return defaultBg;
  }

  if ($counted) {
    return BackgroundColorByStatus[$status] ?? defaultBg;
  }

  return defaultBg;
};

type BackgroundIconProps = {
  $isClickable: boolean;
  $isHighlighted?: boolean;
};

const BackgroundIcon = styled(GRGiftSvg)<BackgroundIconProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  color: var(--gr-chip-bg-color);
  transition: color ${DeltaTransitionsDurations[GRNotificationTypes.BETSLIP_UPDATE] / 4}ms ease-in-out;

  & path {
    transition: filter 300ms ease-in-out;
  }

  ${({ $isClickable }) =>
    $isClickable
      ? css`
          cursor: pointer;
        `
      : undefined}

  ${({ $isHighlighted }) =>
    $isHighlighted
      ? css`
          & path {
            filter: brightness(110%) drop-shadow(0px 0px 2em var(--gr-chip-bg-color));
          }
        `
      : undefined}
`;

const ChipIcon = styled.div<BackgroundIconProps>`
  svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    color: var(--gr-chip-bg-color);
    transition: color ${DeltaTransitionsDurations[GRNotificationTypes.BETSLIP_UPDATE] / 4}ms ease-in-out;

    & path {
      transition: filter 300ms ease-in-out;
    }

    ${({ $isClickable }) =>
      $isClickable
        ? css`
            cursor: pointer;
          `
        : undefined}
  }
`;

type WrapperProps = {
  $status?: GRChipStatus;
  $counted: boolean;
  $isClickable: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  position: relative;
  width: 100%;
  height: 5em;

  transition: none 150ms ease-in-out;
  transition-property: background-color, opacity;

  &:not(:last-child) {
    margin-bottom: 0.5em;
  }

  &:nth-child(even) {
    & ${BackgroundIcon} {
      transform: rotateY(180deg);
    }
  }

  ${!Target.__MOBILE__
    ? css`
        &:hover {
          & path {
            /* filter: brightness(110%) drop-shadow(0px 0px 2em var(--gr-chip-bg-color)); */
            filter: brightness(110%);
          }
        }
      `
    : undefined}

  ${({ $status, $counted, $isClickable }) => css`
    --gr-chip-bg-color: ${getBackground({ $status, $counted })};

    opacity: ${!$status ? 0.75 : 1};

    ${$isClickable
      ? css`
          cursor: pointer;
        `
      : css`
          pointer-events: none;
        `}

    ${!Target.__MOBILE__
      ? css`
          &:hover {
            &:before {
              opacity: 1;
            }
          }
        `
      : undefined}
  `}
`;

const LabelWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -40%);

  text-align: center;
  line-height: 1em;

  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DefaultLabelTop = styled.div`
  font-size: 80%;
`;

const DefaultLabelBottom = styled.div`
  font-size: 110%;
  font-weight: bold;
`;

const circleSpreadAnim = keyframes`
  0% {
    opacity: 0;
    transform: scale(0);
  }

  80% {
    opacity: 0.75;
    transform: scale(1);
  }

  100% {
    opacity: 0;
    transform: scale(1.2);
  }
`;

type CircleSpreadProps = {
  $width: number;
  $height: number;
  $top: number;
  $left: number;
};

const CircleSpread = styled.div<CircleSpreadProps>`
  position: absolute;
  pointer-events: none;
  z-index: 1;

  ${({ $width, $height, $top, $left }) => {
    const scaleFactor = Target.__MOBILE__ ? 1.3 : 1.2;
    const scaledWidth = $width + (scaleFactor - 1) * $width;
    const scaledHeight = $height + (scaleFactor - 1) * $height;
    const offsetLeft = (scaledWidth - $width) / 2;
    const offsetTop = (scaledHeight - $height) / 2;

    const boxShadowDim = 0.4 * scaleFactor; // em

    return css`
      width: ${scaledWidth}px;
      height: ${scaledHeight}px;
      left: calc(${$left - offsetLeft}px);
      top: calc(${$top - offsetTop}px + 0.15em);
      box-shadow: 0px 0px 1px ${boxShadowDim}em #ffffff55;
    `;
  }}

  background: transparent;
  border-radius: 50%;
  filter: drop-shadow(0 0 0.3em #ffffff55);

  animation: ${circleSpreadAnim} ${DeltaTransitionsDurations[GRNotificationTypes.BETSLIP_UPDATE]}ms
    cubic-bezier(0.39, 0.575, 0, 1) forwards;
`;

const transformsSharedStyle = css`
  position: relative;
  width: 100%;
  height: 100%;
`;

const scaleAnim = keyframes`
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
  }
`;

type ScaleAnimationProps = {
  $isWon: boolean;
  $shouldAnimate: boolean;
};

const ScaleAnimation = styled.div<ScaleAnimationProps>`
  ${transformsSharedStyle}
  overflow: hidden;

  ${({ $isWon, $shouldAnimate }) => {
    if ($isWon && $shouldAnimate) {
      return css`
        animation: ${scaleAnim} ${DeltaTransitionsDurations[GRNotificationTypes.BETSLIP_UPDATE] / 4}ms ease-in-out
          forwards;
      `;
    }
  }}
`;

type GRChipProps = {
  className?: string;
  index: number;
  status?: GRChipStatus;
  counted: boolean;
  isLast: boolean;
  ChipImage?: React.FunctionComponent<React.SVGProps<SVGSVGElement>> | null;
  betslipReference?: string;
  shouldAnimate: boolean;
  statusAnimation?: string | null;
};

const GRChip = ({
  className = '',
  index,
  counted,
  status,
  isLast,
  ChipImage,
  betslipReference,
  shouldAnimate = false,
}: GRChipProps) => {
  const wrapperNodeRef = useRef<HTMLDivElement>(null);
  const { onChipClick, lastSelectedChipIndex, chipDetailsPanelIsOpened } = useGRRoundContext();

  const Content = useMemo(() => {
    let rendered: ReactElement | null = null;

    if (!status) {
      if (!isLast) {
        rendered = (
          <LabelWrapper>
            <DefaultLabelTop>{__('Bet')}</DefaultLabelTop>
            <DefaultLabelBottom>{index + 1}</DefaultLabelBottom>
          </LabelWrapper>
        );
      } else {
        rendered = (
          <LabelWrapper>
            <DefaultLabelTop>. . .</DefaultLabelTop>
          </LabelWrapper>
        );
      }
    } else if (!ChipImage) {
      rendered = <GRChipIcon status={status} counted={counted} />;
    }

    return () => rendered;
  }, [status, ChipImage, isLast, index, counted]);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (chipDetailsPanelIsOpened === false) {
        e.stopPropagation();

        if (onChipClick) {
          onChipClick(index);
        }
      }
    },
    [chipDetailsPanelIsOpened, index, onChipClick],
  );

  const isHighlighted = useMemo(
    () => lastSelectedChipIndex === index && chipDetailsPanelIsOpened,
    [chipDetailsPanelIsOpened, index, lastSelectedChipIndex],
  );

  const isClickable = useMemo(() => !!onChipClick && !!status, [onChipClick, status]);

  const [viewWrapperElem, setViewWrapperElem] = useState<HTMLElement>();

  useLayoutEffect(() => {
    const node = document.getElementById('gr-view-wrapper');

    if (node) {
      setViewWrapperElem(node);
    }
  }, []);

  const circleSpreadClientRelativeLocation = useMemo(() => {
    if (status === GRChipStatuses.WON) {
      const { left: viewWrapperLeft, top: viewWrapperTop } = viewWrapperElem?.getBoundingClientRect() ?? {};

      if (typeof viewWrapperLeft === 'number' && typeof viewWrapperTop === 'number') {
        const {
          width: wrapperWidth,
          height: wrapperHeight,
          left: wrapperLeft,
          top: wrapperTop,
        } = wrapperNodeRef.current?.getBoundingClientRect() ?? {};

        if (
          typeof wrapperWidth === 'number' &&
          typeof wrapperHeight === 'number' &&
          typeof wrapperLeft === 'number' &&
          typeof wrapperTop === 'number'
        ) {
          return {
            left: wrapperLeft - viewWrapperLeft,
            top: wrapperTop - viewWrapperTop,
            width: wrapperWidth,
            height: wrapperHeight,
          };
        }
      }
    }
  }, [status, viewWrapperElem]);

  const isWon = useMemo(() => status === GRChipStatuses.WON && counted, [status, counted]);

  const [hasJustWon, setHasJustWon] = useState(false);
  const spreadAppearanceTimeoutId = useRef<NodeJS.Timeout | null>();
  const spreadDisappearanceTimeoutId = useRef<NodeJS.Timeout | null>();

  const [hasJustLost, setHasJustLost] = useState(false);
  const lostSoundAppearanceTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const lostSoundDisappearanceTimeoutId = useRef<NodeJS.Timeout | null>();

  useLayoutEffect(() => {
    if (status === GRChipStatuses.WON && counted && shouldAnimate) {
      if (!spreadAppearanceTimeoutId.current) {
        spreadAppearanceTimeoutId.current = setTimeout(() => {
          setHasJustWon(true);
        }, 0);
      }

      if (!spreadDisappearanceTimeoutId.current) {
        spreadDisappearanceTimeoutId.current = setTimeout(() => {
          setHasJustWon(false);
        }, DeltaTransitionsDurations[GRNotificationTypes.BETSLIP_UPDATE]);
      }
    }

    if (status === GRChipStatuses.LOST && counted && shouldAnimate) {
      if (!lostSoundAppearanceTimeoutId.current) {
        lostSoundAppearanceTimeoutId.current = setTimeout(() => {
          setHasJustLost(true);
        }, 0);
      }

      if (!lostSoundDisappearanceTimeoutId.current) {
        lostSoundDisappearanceTimeoutId.current = setTimeout(() => {
          setHasJustLost(false);
        }, DeltaTransitionsDurations[GRNotificationTypes.BETSLIP_UPDATE]);
      }
    }
  }, [status, counted, shouldAnimate]);

  useLayoutEffect(() => {
    return () => {
      if (spreadAppearanceTimeoutId.current) {
        clearTimeout(spreadAppearanceTimeoutId.current);
      }

      if (spreadDisappearanceTimeoutId.current) {
        clearTimeout(spreadDisappearanceTimeoutId.current);
      }

      if (lostSoundAppearanceTimeoutId.current) {
        clearTimeout(lostSoundAppearanceTimeoutId.current);
      }

      if (lostSoundDisappearanceTimeoutId.current) {
        clearTimeout(lostSoundDisappearanceTimeoutId.current);
      }
    };
  }, []);

  useEffect(() => {
    if (hasJustWon) {
      SoundPlayer.load(SoundIds.GR_WIN_CHIP).then((s) => {
        s.play();
      });
    }
  }, [hasJustWon]);

  useEffect(() => {
    if (hasJustLost) {
      SoundPlayer.load(SoundIds.GR_LOST_CHIP).then((s) => {
        s.play();
      });
    }
  }, [hasJustLost]);

  return (
    <>
      <Wrapper
        ref={wrapperNodeRef}
        className={className}
        data-anchorid={betslipReference ? `gr-chip-${betslipReference}` : undefined}
        $status={status}
        $counted={counted}
        $isClickable={isClickable}
        onClick={onClick}
      >
        <ScaleAnimation $isWon={isWon} $shouldAnimate={shouldAnimate}>
          <Content />
          <ChipIcon $isClickable={isClickable}>
            {ChipImage ? <ChipImage /> : <BackgroundIcon $isClickable={isClickable} $isHighlighted={isHighlighted} />}
          </ChipIcon>
        </ScaleAnimation>
      </Wrapper>
      {hasJustWon && viewWrapperElem
        ? createPortal(
            <CircleSpread
              $width={circleSpreadClientRelativeLocation?.width ?? 0}
              $height={circleSpreadClientRelativeLocation?.height ?? 0}
              $top={circleSpreadClientRelativeLocation?.top ?? 0}
              $left={circleSpreadClientRelativeLocation?.left ?? 0}
            />,
            viewWrapperElem,
          )
        : null}
    </>
  );
};

export default GRChip;
